import React, { useEffect, useState, useCallback } from 'react';
import pb from '../lib/pocketbase';
import { Outlet, NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faSignOutAlt, faTachometerAlt, faStar, faEnvelope,
  faProjectDiagram, faBars, faTimes, faHardHat, faClipboardList,
  faBoxes, faClock, faUserTie, faCalendarAlt, faMapMarkedAlt, faFileSignature,
  faSun, faMoon, faGlobe, faPalette, faUserShield, faIdCard, faImages
} from '@fortawesome/free-solid-svg-icons';
import styles from './Dashboard.module.css';
import ProjectForm from '../components/dashboard/ProjectForm';
import MessageModal from '../components/dashboard/MessageModal';


function DashboardLayout() {
  const navigate = useNavigate();
  const { openAuthModal, user } = useAuth();
  const { theme, toggleTheme, primaryColor, setPrimaryColor, themes } = useTheme();
  const location = useLocation();
  const userRole = user?.role || '';

  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState({
    userCount: 0,
    pendingReviews: 0,
    unreadMessages: 0,
    projectCount: 0,
    averageProgress: 0,
    lowStockCount: 0,
    pendingApplications: 0
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);

  const fetchData = useCallback(async (signal) => {
    try {
      const promises = [];
      const fetchTypes = [];

      // Everyone sees Projects (Realizations)
      promises.push(pb.collection('projects').getFullList({ sort: '-created', signal }));
      fetchTypes.push('projects');

      // Admin & Foreman data access
      if (userRole === 'admin' || userRole === 'foreman' || userRole === 'bureau') {
        promises.push(pb.collection('users').getFullList({ sort: '-created', signal }));
        fetchTypes.push('users');
      }

      // Admin, Foreman, Worker data
      if (userRole === 'admin' || userRole === 'foreman' || userRole === 'worker' || userRole === 'employee') {
        const chantierFilter = userRole === 'admin' ? '' : `chef_chantier ?~ "${user.id}" || workers ?~ "${user.id}"`;
        promises.push(pb.collection('chantiers').getFullList({ sort: '-created', filter: chantierFilter, signal }).catch(() => []));
        fetchTypes.push('chantiers');
      }

      // Admin & Bureau specific data
      if (userRole === 'admin' || userRole === 'bureau') {
        promises.push(pb.collection('reviews').getFullList({ sort: '-created', expand: 'user', signal }).catch(() => []));
        fetchTypes.push('reviews');

        promises.push(pb.collection('messages').getFullList({ sort: '-created', expand: 'sender', signal }).catch(() => []));
        fetchTypes.push('messages');

        promises.push(pb.collection('inventory').getFullList({ sort: '-created', signal }).catch(() => []));
        fetchTypes.push('inventory');
      }

      const results = await Promise.all(promises);

      let projectRecords = [];
      let userRecords = [];
      let reviewRecords = [];
      let messageRecords = [];
      let chantierRecords = [];
      let inventoryRecords = [];

      results.forEach((res, index) => {
        const type = fetchTypes[index];
        if (type === 'projects') projectRecords = res;
        if (type === 'users') userRecords = res;
        if (type === 'reviews') reviewRecords = res;
        if (type === 'messages') messageRecords = res;
        if (type === 'chantiers') chantierRecords = res;
        if (type === 'inventory') inventoryRecords = res;
      });

      const projectsWithImageUrls = projectRecords.map(project => {
        let coverImageUrl = null;
        if (project.cover_image) {
          try {
            coverImageUrl = pb.files.getURL(project, project.cover_image, { thumb: '400x400' });
          } catch (e) {
            console.error(`Error generating URL for project ${project.id}:`, e);
          }
        }
        return { ...project, coverImageUrl };
      });

      setProjects(projectsWithImageUrls);

      if (userRole === 'admin' || userRole === 'foreman' || userRole === 'bureau') {
        setUsers(userRecords);
      }

      if (userRole === 'admin' || userRole === 'bureau') {
        setReviews(reviewRecords);
        setMessages(messageRecords);
        setChantiers(chantierRecords);
        setInventory(inventoryRecords);

        const pendingReviewsCount = reviewRecords.filter(r => r.status === 'pending').length;
        const unreadMessagesCount = messageRecords.filter(m => !m.is_read).length;

        // Calculate Average Progress
        const activeChantiers = chantierRecords.filter(c => (c.progress || 0) < 100);
        const avgProgress = activeChantiers.length > 0
          ? Math.round(activeChantiers.reduce((acc, c) => acc + (c.progress || 0), 0) / activeChantiers.length)
          : 0;

        // Low Stock Count
        const lowStockCount = inventoryRecords.filter(item => (item.quantity || 0) <= (item.min_stock || 5)).length;

        // Pending Applications from users collection (strictly candidates)
        const pendingAppsCount = userRecords.filter(u => u.profile_type === 'candidate' && u.application_status === 'NOUVEAU').length;

        setStats({
          userCount: userRecords.length,
          pendingReviews: pendingReviewsCount,
          unreadMessages: unreadMessagesCount,
          projectCount: projectRecords.length,
          averageProgress: avgProgress,
          lowStockCount: lowStockCount,
          pendingApplications: pendingAppsCount
        });
      }

    } catch (error) {
      if (error.name === 'AbortError' || error.isAbort) return;
      console.error("Failed to fetch dashboard data:", error);
    }
  }, [userRole]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    // Allowed roles for this layout
    const allowedRoles = ['admin', 'foreman', 'worker', 'client', 'employee'];

    if (!pb.authStore.isValid || !allowedRoles.includes(userRole)) {
      navigate('/');
      openAuthModal('login');
      return;
    }

    fetchData(signal);

    return () => {
      controller.abort();
    };
  }, [navigate, fetchData, userRole]);

  const handleOpenForm = (project = null) => {
    setEditingProject(project);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingProject(null);
  };

  const handleLogout = () => {
    pb.authStore.clear();
    navigate('/login');
  };

  const handleSave = (savedProject) => {
    if (savedProject) {
      fetchData();
      toast.success(`Projet "${savedProject.title}" sauvegardé avec succès !`);
    }
    handleCloseForm();
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await pb.collection('projects').delete(projectId);
      setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
      toast.success('Projet supprimé avec succès !');
      fetchData();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error(`Erreur lors de la suppression : ${error.message}`);
    }
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      pb.collection('messages').update(message.id, { is_read: true })
        .then(() => fetchData());
    }
  };

  const handleCloseModal = () => {
    setSelectedMessage(null);
  };

  const handleReply = (message, replyText) => {
    console.log(`Réponse à ${message.expand?.sender?.email || 'N/A'}: ${replyText}`);
    alert('La fonctionnalité de réponse n\'est pas encore connectée.');
    handleCloseModal();
  };

  // Roles labels mapping
  const roleLabels = {
    'admin': 'Administrateur',
    'foreman': 'Chef de Chantier',
    'worker': 'Ouvrier',
    'employee': 'Collaborateur',
    'client': 'Espace Client'
  };

  if (!pb.authStore.isValid || !['admin', 'foreman', 'worker', 'client', 'employee'].includes(userRole)) {
    return null;
  }

  if (!user) {
    return <div className={styles.loading}>Chargement du dashboard...</div>;
  }

  return (
    <div className={styles.pageWrapper}>
      {isFormOpen && (
        <ProjectForm
          project={editingProject}
          onSave={handleSave}
          onCancel={handleCloseForm}
        />
      )}

      {selectedMessage && (
        <MessageModal
          message={selectedMessage}
          onClose={handleCloseModal}
          onReply={handleReply}
        />
      )}

      <div className={styles.dashboardContainer}>
        {/* --- MOBILE TOP NAV --- */}
        <div className={styles.mobileTopNav}>
          <div className={styles.mobileTopNavLeft}>
            <button onClick={() => setIsMobileMenuOpen(true)} className={styles.mobileTopNavMenuBtn}>
              <FontAwesomeIcon icon={faBars} />
            </button>
            <span className={styles.mobileTopNavTitle}>Admin</span>
          </div>
          <div className={styles.mobileTopNavRight}>
            <button onClick={toggleTheme} className={styles.themeToggleBtn} title="Mode Jour/Nuit">
              <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
            </button>
          </div>
        </div>

        <div className={styles.dashboardLayout}>
          {/* --- DESKTOP SIDEBAR --- */}
          <aside className={styles.sidebar}>
            <h1 className={styles.sidebarTitle}>Maison Quinard</h1>
            <div className={styles.userRoleBadge}>{roleLabels[userRole] || 'Accès Pro'}</div>

            <nav className={styles.sidebarNav}>
              {/* GROUPE 1: BASE DE DONNÉES / SYSTÈME */}
              <div className={styles.navGroup}>
                <div className={styles.navSeparator}>Administration</div>
                {userRole === 'admin' && (
                  <>
                    <li><NavLink to="/admin" end className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faTachometerAlt} /> Vue d'ensemble</NavLink></li>
                    <li><NavLink to="/admin/users" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faUserShield} /> Utilisateurs</NavLink></li>
                  </>
                )}
                {(userRole === 'admin' || userRole === 'foreman' || userRole === 'worker' || userRole === 'employee') && (
                  <li><NavLink to="/admin/chantiers" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faHardHat} /> Chantiers</NavLink></li>
                )}
                {userRole === 'admin' && (
                  <li><NavLink to="/admin/customers" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faUsers} /> Clients</NavLink></li>
                )}
              </div>

              {/* GROUPE 2: GESTION OPÉRATIONNELLE */}
              <div className={styles.navGroup}>
                <div className={styles.navSeparator}>Opérationnel</div>
                {userRole === 'admin' && (
                  <li><NavLink to="/admin/team" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faUserTie} /> Équipe</NavLink></li>
                )}
                {(userRole === 'admin' || userRole === 'foreman' || userRole === 'worker' || userRole === 'employee') && (
                  <>
                    <li><NavLink to="/admin/timesheets" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faClock} /> Feuilles d'heures</NavLink></li>
                    <li><NavLink to="/admin/planning" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faCalendarAlt} /> Planning</NavLink></li>
                    <li><NavLink to="/admin/map" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faMapMarkedAlt} /> Carte chantier</NavLink></li>
                    <li><NavLink to="/admin/inventory" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faBoxes} /> Stock</NavLink></li>
                  </>
                )}
              </div>

              {/* GROUPE 3: COMMUNICATION & DOCUMENTS */}
              <div className={styles.navGroup}>
                <div className={styles.navSeparator}>Communication & Fichiers</div>
                {userRole === 'admin' && (
                  <>
                    <li><NavLink to="/admin/recrutement" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faIdCard} /> Recrutement</NavLink></li>
                    <li><NavLink to="/admin/messages" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faEnvelope} /> Message</NavLink></li>
                    <li><NavLink to="/admin/reviews" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faStar} /> Avis client</NavLink></li>
                    <li><NavLink to="/admin/projects" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faProjectDiagram} /> Réalisation</NavLink></li>
                    <li><NavLink to="/admin/website" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faImages} /> Accueil</NavLink></li>
                  </>
                )}
                <li><NavLink to="/admin/documents" className={({ isActive }) => isActive ? styles.active : ''}><FontAwesomeIcon icon={faFileSignature} /> Mes documents</NavLink></li>
              </div>
            </nav>

            <div className={styles.sidebarFooter}>
              <Link to="/" className={styles.sidebarActionBtn} title="Retour au site public">
                <FontAwesomeIcon icon={faGlobe} /> Retour au site
              </Link>
              <button onClick={toggleTheme} className={styles.sidebarActionBtn} title="Changer le thème">
                <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} /> {theme === 'light' ? 'Mode Sombre' : 'Mode Clair'}
              </button>
              <div className={styles.navSeparator} style={{ margin: '10px 0' }}></div>
              <button onClick={handleLogout} className={styles.logoutBtn}>
                <FontAwesomeIcon icon={faSignOutAlt} /> Déconnexion
              </button>
            </div>
          </aside>

          <main className={styles.mainContent}>
            <div className={styles.header}>
              <div className={styles.adminInfo}>
                {/* Empty div kept for alignment if needed, or remove */}
              </div>
              <div className={styles.headerActions}>
                {/* Theme Color Dropdown */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowThemeDropdown(!showThemeDropdown)}
                    className={styles.themeToggleBtn}
                    title="Changer le thème"
                    style={{
                      marginRight: '10px',
                      background: showThemeDropdown ? (themes[primaryColor] || themes['default']).primary : 'transparent',
                      color: showThemeDropdown ? 'white' : 'inherit'
                    }}
                  >
                    <FontAwesomeIcon icon={faPalette} />
                  </button>
                  {showThemeDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '8px',
                      background: theme === 'dark' ? '#1e293b' : 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      zIndex: 100,
                      minWidth: '280px',
                      padding: '1rem'
                    }}>
                      <div style={{
                        marginBottom: '1rem',
                        paddingBottom: '0.75rem',
                        borderBottom: '1px solid var(--color-light-gray)'
                      }}>
                        <h4 style={{
                          margin: 0,
                          fontSize: '0.95rem',
                          fontWeight: '700',
                          color: theme === 'dark' ? '#f1f5f9' : '#334155'
                        }}>
                          <FontAwesomeIcon icon={faPalette} style={{ marginRight: '8px' }} />
                          Choisir un thème
                        </h4>
                      </div>
                      <div style={{ display: 'grid', gap: '0.75rem' }}>
                        {Object.entries(themes).map(([key, themeOption]) => (
                          <button
                            key={key}
                            onClick={() => {
                              setPrimaryColor(key);
                              setShowThemeDropdown(false);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              padding: '0.75rem 1rem',
                              border: `2px solid ${primaryColor === key ? themeOption.primary : '#e2e8f0'}`,
                              borderRadius: '6px',
                              background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : (primaryColor === key ? '#f8fafc' : 'white'),
                              cursor: 'pointer',
                              color: theme === 'dark' ? '#cbd5e1' : '#475569',
                              textAlign: 'left',
                              fontSize: '0.9rem',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (primaryColor !== key) {
                                e.currentTarget.style.borderColor = themeOption.primary;
                                e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.1)' : '#f1f5f9';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (primaryColor !== key) {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'white';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '1.5rem' }}>{themeOption.icon}</span>
                              <span style={{
                                fontWeight: primaryColor === key ? '600' : '500'
                              }}>
                                {themeOption.name}
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <span style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                background: themeOption.primary,
                                border: '2px solid white',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}></span>
                              <span style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                background: themeOption.accent,
                                border: '2px solid white',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }}></span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button onClick={toggleTheme} className={styles.themeToggleBtn} title="Mode Jour/Nuit">
                  <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
                </button>
                <Link to="/" className={styles.backToSiteBtn} title="Retour au site">
                  <FontAwesomeIcon icon={faGlobe} /> Site Public
                </Link>
              </div>
            </div>

            <Outlet context={{
              users,
              reviews,
              messages,
              projects,
              stats,
              chantiers,
              inventory,
              handleDeleteProject,
              handleViewMessage,
              setIsFormOpen,
              setEditingProject,
              handleOpenForm,
              refreshData: fetchData,
              userRole // Pass role to children
            }} />
          </main>
        </div>
      </div>

      {/* --- MOBILE DRAWER (Tiroir) --- */}
      <div className={`${styles.mobileDrawer} ${isMobileMenuOpen ? styles.drawerOpen : ''}`}>
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>Menu</h2>
          <button onClick={() => setIsMobileMenuOpen(false)} className={styles.closeDrawerBtn}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <nav className={styles.drawerNav}>
          {/* GROUPE 1: BASE DE DONNÉES / SYSTÈME */}
          <div className={styles.navGroup}>
            <div className={styles.navSeparator}>Administration</div>
            {userRole === 'admin' && (
              <>
                <li><NavLink to="/admin" end className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faTachometerAlt} /> Vue d'ensemble</NavLink></li>
                <li><NavLink to="/admin/users" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faUserShield} /> Utilisateurs</NavLink></li>
              </>
            )}
            {(userRole === 'admin' || userRole === 'foreman' || userRole === 'worker' || userRole === 'employee') && (
              <li><NavLink to="/admin/chantiers" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faHardHat} /> Chantiers</NavLink></li>
            )}
            {userRole === 'admin' && (
              <li><NavLink to="/admin/customers" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faUsers} /> Clients</NavLink></li>
            )}
          </div>

          {/* GROUPE 2: GESTION OPÉRATIONNELLE */}
          <div className={styles.navGroup}>
            <div className={styles.navSeparator}>Opérationnel</div>
            {userRole === 'admin' && (
              <li><NavLink to="/admin/team" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faUserTie} /> Équipe</NavLink></li>
            )}
            {(userRole === 'admin' || userRole === 'foreman' || userRole === 'worker' || userRole === 'employee') && (
              <>
                <li><NavLink to="/admin/timesheets" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faClock} /> Feuilles d'heures</NavLink></li>
                <li><NavLink to="/admin/planning" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faCalendarAlt} /> Planning</NavLink></li>
                <li><NavLink to="/admin/map" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faMapMarkedAlt} /> Carte chantier</NavLink></li>
                <li><NavLink to="/admin/inventory" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faBoxes} /> Stock</NavLink></li>
              </>
            )}
          </div>

          {/* GROUPE 3: COMMUNICATION & DOCUMENTS */}
          <div className={styles.navGroup}>
            <div className={styles.navSeparator}>Communication & Fichiers</div>
            {userRole === 'admin' && (
              <>
                <li><NavLink to="/admin/recrutement" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faIdCard} /> Recrutement</NavLink></li>
                <li><NavLink to="/admin/messages" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faEnvelope} /> Message</NavLink></li>
                <li><NavLink to="/admin/reviews" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faStar} /> Avis client</NavLink></li>
                <li><NavLink to="/admin/projects" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faProjectDiagram} /> Réalisation</NavLink></li>
                <li><NavLink to="/admin/website" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faImages} /> Accueil</NavLink></li>
              </>
            )}
            <li><NavLink to="/admin/documents" className={({ isActive }) => isActive ? styles.active : ''} onClick={() => setIsMobileMenuOpen(false)}><FontAwesomeIcon icon={faFileSignature} /> Mes documents</NavLink></li>
          </div>
        </nav>
        <div className={styles.drawerFooter}>
          <Link to="/" className={styles.sidebarActionBtn} title="Retour au site public" onClick={() => setIsMobileMenuOpen(false)}>
            <FontAwesomeIcon icon={faGlobe} /> Retour au site
          </Link>
          <button onClick={toggleTheme} className={styles.sidebarActionBtn} title="Changer le thème">
            <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} /> {theme === 'light' ? 'Mode Sombre' : 'Mode Clair'}
          </button>
          <div className={styles.navSeparator} style={{ margin: '10px 0' }}></div>
          <button onClick={handleLogout} className={styles.logoutBtn}>
            <FontAwesomeIcon icon={faSignOutAlt} /> Déconnexion
          </button>
        </div>
      </div>

      {/* --- MOBILE OVERLAY --- */}
      {isMobileMenuOpen && <div className={styles.drawerOverlay} onClick={() => setIsMobileMenuOpen(false)}></div>}

      {/* --- MOBILE BOTTOM DOCK (Native App Style) --- */}
      <div className={styles.mobileBottomDock}>
        <NavLink to="/admin" end className={({ isActive }) => isActive ? styles.active : ''}>
          <FontAwesomeIcon icon={faTachometerAlt} />
          <span>Accueil</span>
        </NavLink>
        <NavLink to="/admin/planning" className={({ isActive }) => isActive ? styles.active : ''}>
          <FontAwesomeIcon icon={faCalendarAlt} />
          <span>Planning</span>
        </NavLink>
        <NavLink to="/admin/chantiers" className={({ isActive }) => isActive ? styles.active : ''}>
          <FontAwesomeIcon icon={faHardHat} />
          <span>Chantiers</span>
        </NavLink>
        <button onClick={() => setIsMobileMenuOpen(true)} className={styles.menuTriggerBtn}>
          <FontAwesomeIcon icon={faBars} />
          <span>Menu</span>
        </button>
      </div>
    </div>
  );
}

export default DashboardLayout;
